import React, { useState, useRef, useEffect } from 'react';
import { Palette, Pipette } from 'lucide-react';
import { useBranding } from '../../context/BrandingContext';

/**
 * Drop-in replacement for `<input type="color">` that surfaces the
 * brand palette saved in `/admin/branding` as quick-pick swatches.
 *
 * Behaviour:
 *  - Click on the color swatch → opens a small popover with:
 *    • the 5 main brand colors (primary, secondary, background,
 *      text, text-secondary)
 *    • all the user-defined `extra_colors` (up to 10)
 *    • a "Custom" option that delegates to a native color picker
 *      so the operator can still pick any hex if needed.
 *  - Hex value is also editable as text on the right (optional).
 *  - Closes on outside click or ESC.
 *
 * Props:
 *  - value:   current hex string (e.g. "#D44E20")
 *  - onChange(hex): called with the new color
 *  - size:    swatch size in px (default 36)
 *  - showHexInput: render the manual hex input next to the swatch (default true)
 *  - testid:  data-testid for the swatch button
 */
export function BrandColorPicker({ value, onChange, size = 36, showHexInput = true, testid, className = '' }) {
  const branding = useBranding();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const nativeRef = useRef(null);
  const swatch = value || '#000000';

  // Close on outside click or escape
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const main = [
    { value: branding.primary_color, label: 'Primario', key: 'primary' },
    { value: branding.secondary_color, label: 'Secondario', key: 'secondary' },
    { value: branding.background_color, label: 'Sfondo', key: 'background' },
    { value: branding.text_color, label: 'Testo', key: 'text' },
    { value: branding.text_secondary_color, label: 'Testo 2°', key: 'text-secondary' },
  ].filter(c => c.value);

  const extras = (branding.extra_colors || [])
    .filter(c => c && c.value)
    .map((c, i) => ({ value: c.value, label: c.label || `Accent ${i + 1}`, key: `extra-${i}` }));

  const pick = (hex) => {
    onChange(hex);
    setOpen(false);
  };

  const isSelected = (hex) => (hex || '').toLowerCase() === (value || '').toLowerCase();

  return (
    <div ref={wrapRef} className={`relative inline-flex items-center gap-2 ${className}`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        data-testid={testid || 'brand-color-swatch'}
        className="rounded-lg border-2 border-slate-200 hover:border-slate-300 transition-colors shadow-sm"
        style={{ width: size, height: size, backgroundColor: swatch }}
        aria-label="Seleziona colore"
      />
      {showHexInput && (
        <input
          type="text"
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          spellCheck={false}
          className="w-24 h-9 bg-slate-50 border border-slate-200 rounded-lg px-2 text-xs font-mono text-[#1A1A1A] text-center focus:border-[#D44E20] focus:outline-none"
        />
      )}

      {/* Hidden native color input for the "Custom" fallback action */}
      <input
        ref={nativeRef}
        type="color"
        value={swatch}
        onChange={e => pick(e.target.value)}
        className="absolute opacity-0 pointer-events-none"
        style={{ width: 0, height: 0 }}
        tabIndex={-1}
      />

      {open && (
        <div
          className="absolute z-50 left-0 top-full mt-2 w-64 bg-white rounded-2xl border border-slate-200 shadow-xl p-3 animate-fade-in-up"
          data-testid="brand-color-popover"
        >
          <div className="flex items-center gap-1.5 mb-2">
            <Palette size={12} className="text-[#D44E20]" />
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[#64748B]">Brand</p>
          </div>
          <div className="grid grid-cols-5 gap-1.5 mb-3">
            {main.map(c => (
              <SwatchButton key={c.key} color={c} selected={isSelected(c.value)} onPick={pick} testid={`swatch-${c.key}`} />
            ))}
          </div>

          {extras.length > 0 && (
            <>
              <div className="flex items-center gap-1.5 mb-2 mt-2 pt-2 border-t border-slate-100">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-[#64748B]">Altri colori</p>
              </div>
              <div className="grid grid-cols-5 gap-1.5 mb-3">
                {extras.map(c => (
                  <SwatchButton key={c.key} color={c} selected={isSelected(c.value)} onPick={pick} testid={`swatch-${c.key}`} />
                ))}
              </div>
            </>
          )}

          <button
            type="button"
            onClick={() => nativeRef.current?.click()}
            data-testid="brand-color-custom"
            className="mt-1 w-full h-8 rounded-lg border border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-xs font-medium text-[#1A1A1A] flex items-center justify-center gap-1.5 transition-colors"
          >
            <Pipette size={12} /> Colore personalizzato…
          </button>
        </div>
      )}
    </div>
  );
}

function SwatchButton({ color, selected, onPick, testid }) {
  return (
    <button
      type="button"
      onClick={() => onPick(color.value)}
      data-testid={testid}
      title={`${color.label} · ${color.value}`}
      className={`relative w-9 h-9 rounded-lg border-2 transition-all hover:scale-110 ${
        selected ? 'border-[#1A1A1A] ring-2 ring-[#D44E20]/30' : 'border-slate-200 hover:border-slate-300'
      }`}
      style={{ backgroundColor: color.value }}
    >
      {selected && (
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="w-1.5 h-1.5 bg-white rounded-full shadow" />
        </span>
      )}
    </button>
  );
}
